<template>
  <div class="body concierge" data-uiMode="1">
    <div class="masthead">
      <DescriptorHeader @returnToMainPage="handleReturnToMainPage" />
    </div>
    <OverlayLoader v-if="isLoading" />
    <main v-else>
      <div class="support-layout">
        <section class="introduction">
          <div class="eyebrow">
            <span></span> {{ capitalizedDescriptor }}
          </div>
          <h1>Help with payments.</h1>
          <p>Questions about a charge or a refund? Send your enquiry to our support team.</p>
        </section>
        <section class="request-card" aria-label="Payment support request">
          <div class="card-heading">
            <span>Contact the support team</span>
          </div>
          <FormSubmissionResult
            v-if="isFormSubmitted"
            :email="submittedEmail"
            v-bind="confirmationText"
            @back="handleReturnToMainPage"
          />
          <SupportForm v-else v-bind="formText" @formSubmitted="handleFormSubmit" />
        </section>
      </div>
      <section id="payment-information" class="payment-strip" aria-label="Payment information">
        <PaymentInfo />
      </section>
      <section id="faq" class="faq-section" aria-label="Frequently asked questions">
        <div class="faq-intro">
          <h2>Questions, answered.</h2>
        </div>
        <FaqBlock title="" :items="faqItems" />
      </section>
    </main>
    <LegalAddressFooter v-if="legalAddress" :legalAddress="legalAddress" />
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'

import { useDescriptorPage } from '@/composables/useDescriptorPage'
import FaqBlock from '@/templates/baseline/components/FaqBlock.vue'
import FormSubmissionResult from '@/templates/baseline/components/FormSubmissionResult.vue'
import LegalAddressFooter from '@/templates/baseline/components/LegalAddressFooter.vue'
import OverlayLoader from '@/templates/baseline/components/OverlayLoader.vue'
import SupportForm from '@/templates/baseline/components/SupportForm.vue'

import DescriptorHeader from './DescriptorHeader.vue'
import PaymentInfo from './PaymentInfo.vue'
import favicon from '../assets/favicon.ico?no-inline'
import { confirmationText, faqItems, formText } from '../content'

useHead({
  link: [{ key: 'favicon', rel: 'icon', type: 'image/x-icon', href: favicon }],
})

const {
  capitalizedDescriptor,
  legalAddress,
  isFormSubmitted,
  isLoading,
  submittedEmail,
  handleFormSubmit,
  handleReturnToMainPage,
} = useDescriptorPage()
</script>

<style scoped lang="scss">
@use '../styles/tokens';
@use '../../fonts';
@use '../../controls';

.concierge {
  @include tokens.tokens;
  @include controls.controls;

  min-height: 100vh;
  color: var(--content-primary);
  background: var(--background-secondary);
  font-family: var(--font-family-paragraph);
}

.masthead {
  color: var(--content-primary-inverse);
  background: var(--color-brand-900);
}

.support-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--sizes-16) var(--sizes-10);
  gap: var(--sizes-16);
}

.introduction {
  position: relative;

  h1 {
    margin: var(--sizes-6) 0;
    text-align: left;
    letter-spacing: var(--font-letter-spacing-2xs);
    font-family: var(--font-family-heading);
    font-size: clamp(var(--font-size-3xl), 4.8vw, var(--font-size-5xl));
    font-weight: 400;
    line-height: 1.14;
  }

  p {
    max-width: 320px;
    color: var(--content-secondary);
    font-size: var(--font-size-s);
    line-height: var(--font-line-height-m);
  }
}

.eyebrow {
  display: flex;
  align-items: center;
  letter-spacing: var(--font-letter-spacing-m);
  text-transform: uppercase;
  color: var(--background-brand);
  font-size: var(--font-size-2xs);
  font-weight: 600;
  gap: 8px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--background-brand);
  }
}

.request-card {
  overflow: hidden;
  align-self: start;
  border: 1px solid var(--border-primary);
  border-radius: var(--r-radius-m);
  background: var(--background-primary);
}

.card-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 var(--sizes-8);
  padding: var(--sizes-6) 0;
  letter-spacing: var(--font-letter-spacing-m);
  border-bottom: 1px solid var(--border-primary);
  font-size: var(--font-size-2xs);
  font-weight: 600;
}

.payment-strip {
  padding: var(--sizes-10);
  border-top: 1px solid var(--border-primary);
  border-bottom: 1px solid var(--border-primary);
  background: var(--background-primary);
}

:deep(.form) {
  padding: var(--sizes-6) var(--sizes-8) var(--sizes-8);
}

:deep(.form .issue-btn) {
  flex: 1 1 100%;
  padding: var(--sizes-3) var(--sizes-4);
  text-align: left;
  border-radius: var(--r-radius-xs);

  &.active {
    border-width: 1px;
    background: var(--background-brand-subtle);
  }
}

:deep(.form input[type='email']) {
  border-radius: var(--r-radius-xs);
}

:deep(.success-status__header),
:deep(.success-status__footer) {
  text-align: left;
}

:deep(.success-status__email) {
  overflow-wrap: anywhere;
}

.faq-section {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  max-width: 1120px;
  margin: 0 auto;
  padding: 64px 40px 80px;
  gap: 64px;
}

.faq-intro h2 {
  margin-top: 20px;
  letter-spacing: -1px;
  font-family: var(--font-family-heading);
  font-size: var(--font-size-3xl);
  font-weight: 400;
  line-height: 1.4;
}

:deep(.faq-container) {
  max-width: none;
}

:deep(.title) {
  margin-bottom: 16px;
  text-align: left;
  font-size: var(--font-size-xs);
  font-weight: 500;
}

:deep(.faq-item) {
  padding: 20px 0;
  border: 0;
  border-bottom: 1px solid var(--border-primary);
  border-radius: 0;
}

:deep(.faq-list) {
  gap: 0;
}

@media (max-width: 760px) {
  .support-layout {
    grid-template-columns: 1fr;
    padding: 36px 20px;
    gap: 32px;
  }

  .introduction h1 {
    font-size: var(--font-size-4xl);
  }

  .faq-section {
    grid-template-columns: 1fr;
    padding: var(--sizes-10) var(--sizes-5);
    gap: 28px;
  }

  .payment-strip {
    padding: var(--sizes-8) var(--sizes-5);
  }

  .card-heading {
    margin: 0 var(--sizes-6);
  }

  :deep(.form) {
    padding: var(--sizes-6);
  }
}
</style>
